import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getDateInRequestFormat } from 'utils/dateUtil';
import { Store } from 'components/common/types/Store.types';
import { Environment } from 'components/common/types/Deal.types';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import useReferenceData from 'components/pages/Planner/hooks/useReferenceData';
import useNetworkReferenceData from 'components/pages/Planner/hooks/useNetworkReferenceData';
import { AvailabilityReportFilter } from 'components/pages/AvailabilityReport/AvailabilityReport.types';
import { LocationItem, FormatItem, NetworkItem } from './AddFilter/AddFilter.types';

export const getFilterUpdatesForReferenceData = (
  referenceOptions: Store['userOptions']['referenceOptions'],
  referenceNetworks: Store['userOptions']['networks'],
  formatsArray: FormatItem[],
  locationsArray: LocationItem[],
  networksArray: NetworkItem[],
): Partial<AvailabilityReportFilter> | null => {
  const toCode = ({ code }: { code: string }): string => code;
  const allFormatItems = Object.values(referenceOptions.format).flat();
  if (!allFormatItems.length && !referenceNetworks.length) return null;

  const validFormatCodes = new Set(allFormatItems.map(toCode));
  const validLocationCodes = new Set(
    Object.values(referenceOptions.location).flatMap((items) => (Array.isArray(items) ? items.map(toCode) : [])),
  );
  const validNetworkCodes = new Set(referenceNetworks.flatMap(({ networks }) => networks?.map(toCode) ?? []));

  const validFormats = formatsArray.filter(({ code }) => validFormatCodes.has(code));
  const validLocations = locationsArray.filter(({ code }) => validLocationCodes.has(code));
  const validNetworks = networksArray.filter(({ code }) => validNetworkCodes.has(code));

  const hasFormatChanges = validFormats.length !== formatsArray.length;
  const hasLocationChanges = validLocations.length !== locationsArray.length;
  const hasNetworkChanges = validNetworks.length !== networksArray.length;

  if (!hasFormatChanges && !hasLocationChanges && !hasNetworkChanges) return null;

  return {
    ...(hasFormatChanges && { formats: validFormats }),
    ...(hasLocationChanges && { locations: validLocations }),
    ...(hasNetworkChanges && { networks: validNetworks }),
  };
};

interface UseAvailabilityReferenceDataProps {
  channelsArray: Environment[];
  formatsArray: FormatItem[];
  locationsArray: LocationItem[];
  networksArray: NetworkItem[];
  mediaType?: DirectSalesMediaType;
  startDate?: string;
  endDate?: string;
  updateFilters: (filters: Partial<AvailabilityReportFilter>) => void;
}

const useAvailabilityReferenceData = ({
  channelsArray,
  formatsArray,
  locationsArray,
  networksArray,
  mediaType,
  startDate,
  endDate,
  updateFilters,
}: UseAvailabilityReferenceDataProps): { shouldFetchData: boolean } => {
  const referenceOptions = useSelector((state: Store) => state.userOptions.referenceOptions);
  const referenceNetworks = useSelector((state: Store) => state.userOptions.networks);
  const { loadReferenceData } = useReferenceData({ isDirectSales: true });

  const shouldFetchData = Boolean(channelsArray.length > 0 && mediaType && startDate && endDate);

  const referenceParams = shouldFetchData
    ? {
        environments: channelsArray,
        mediaType,
        startDate: getDateInRequestFormat(startDate!, [0, 0, 0, 0]),
        endDate: getDateInRequestFormat(endDate!, [23, 59, 59, 999]),
        isDirectSales: true,
      }
    : { environments: [], mediaType: undefined, startDate: undefined, endDate: undefined };

  const channelCodes = channelsArray.map((c) => c.code).join(',');
  const refDataKey = shouldFetchData ? `${channelCodes}|${mediaType}|${startDate}|${endDate}` : '';

  useEffect(() => {
    if (!refDataKey) return;
    loadReferenceData(referenceParams);
  }, [refDataKey, loadReferenceData]);

  useNetworkReferenceData(referenceParams);

  useEffect(() => {
    if (!shouldFetchData) return;

    const filterUpdates = getFilterUpdatesForReferenceData(
      referenceOptions,
      referenceNetworks,
      formatsArray,
      locationsArray,
      networksArray,
    );

    if (filterUpdates) updateFilters(filterUpdates);
  }, [referenceOptions, referenceNetworks]);

  return { shouldFetchData };
};

export default useAvailabilityReferenceData;
